// iter738 · Shared team-color helmet chip — pulled out of DailyBrief.jsx
// so HomeLiveGames (the "Live Now" strip) can use the exact same real
// 3D-photo recolor instead of its own flat colored Disk. See the fuller
// writeup that used to sit inline in DailyBrief.jsx: this recolors ONE
// neutral helmet photo per team on a canvas (overlay-blend the shell's
// grayscale/gamma luma against a flat team-color fill, masked to just the
// glossy shell so the chrome facemask/hardware stays chrome), instead of
// shipping a separate image per team.
import { useEffect, useState } from 'react';

// iter795 · Deterministic, identity-neutral helmet color for a team we
// don't have real brand colors for yet (a CFBD API hiccup/quota miss,
// or a school we haven't onboarded). Shared so every helmet call site
// degrades the same way instead of each one falling back to something
// different — critically, this must never be "show the real logo
// image instead" (see TeamHelmet's fallback comment): a college team's
// logo can't be displayed without a rights agreement, so the fallback
// has to stay a colored helmet, just not that team's real color.
export const hashColor = (seed = '') => {
  let h = 0;
  for (let i = 0; i < seed.length; i += 1) h = (h * 31 + seed.charCodeAt(i)) & 0xffffff;
  const hue = Math.abs(h) % 360;
  return `hsl(${hue}, 42%, 34%)`;
};
const HELMET_BASE_URI = "https://customer-assets-rejwkqb3.emergentagent.net/job_jay-pernell-sports/artifacts/dr0mfyi6_helmet-base.webp";
const HELMET_LUMA_URI = "https://customer-assets-rejwkqb3.emergentagent.net/job_jay-pernell-sports/artifacts/ompwg6ce_helmet-luma.webp";
const HELMET_MASK_URI = "https://customer-assets-rejwkqb3.emergentagent.net/job_jay-pernell-sports/artifacts/zjymxxhw_helmet-mask.png";

let _helmetAssetsPromise = null;
const _loadImage = (src) => new Promise((resolve, reject) => {
  const im = new Image();
  im.crossOrigin = 'anonymous'; // required: helmet assets are cross-origin, canvas.toDataURL() taints without this
  im.onload = () => resolve(im);
  im.onerror = reject;
  im.src = src;
});
const _loadHelmetAssets = () => {
  if (!_helmetAssetsPromise) {
    _helmetAssetsPromise = Promise.all([
      _loadImage(HELMET_BASE_URI), _loadImage(HELMET_LUMA_URI), _loadImage(HELMET_MASK_URI),
    ]);
  }
  return _helmetAssetsPromise;
};

const _helmetTintCache = new Map();
const _tintHelmet = async (color) => {
  if (_helmetTintCache.has(color)) return _helmetTintCache.get(color);
  const [base, luma, mask] = await _loadHelmetAssets();
  const w = base.width, h = base.height;

  const tint = document.createElement('canvas');
  tint.width = w; tint.height = h;
  const tctx = tint.getContext('2d');
  tctx.drawImage(luma, 0, 0);
  tctx.globalCompositeOperation = 'overlay';
  tctx.fillStyle = color;
  tctx.fillRect(0, 0, w, h);
  tctx.globalCompositeOperation = 'destination-in';
  tctx.drawImage(mask, 0, 0);

  const out = document.createElement('canvas');
  out.width = w; out.height = h;
  const octx = out.getContext('2d');
  octx.drawImage(base, 0, 0);
  octx.drawImage(tint, 0, 0);

  const dataUrl = out.toDataURL('image/png');
  _helmetTintCache.set(color, dataUrl);
  return dataUrl;
};

// Tailwind's `w-6`/`w-7` are fixed rem sizes — resolve them to real px so
// the decal's font-size can be computed in px too. CSS `font-size: X%` is
// relative to the INHERITED font-size, not the element's own box, so a
// helmet in a small box was rendering its label at a few px regardless of
// the box size.
export const SIZE_PX = { 'w-6 h-6': 24, 'w-7 h-7': 28, 'w-8 h-8': 32, 'w-9 h-9': 36, 'w-12 h-12': 48, 'w-14 h-14': 56 };

export const TeamHelmetPhoto = ({ color, abbr, className, sizePx = 24 }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let alive = true;
    _tintHelmet(color).then((url) => { if (alive) setSrc(url); }).catch(() => {});
    return () => { alive = false; };
  }, [color]);
  const label = (abbr || '').slice(0, 4).toUpperCase();
  // Step the size AND anchor down as the abbreviation gets longer
  // (OU/TX vs ARK/LSU vs a 4-letter one like TXST/PITT) so it always
  // stays fully inside the shell instead of overflowing past its edge —
  // a longer label needs both a smaller font and to sit a bit further
  // right, since it's centered on a wider string.
  const factor = label.length <= 2 ? 0.16 : label.length === 3 ? 0.12 : 0.085;
  const leftPct = label.length <= 2 ? 24 : label.length === 3 ? 26 : 29;
  return (
    <div className={`${className} relative`} data-testid="team-helmet-chip">
      {src
        ? <img src={src} alt="" className="w-full h-full object-contain" />
        : <div className="w-full h-full rounded-full bg-white/10" />}
      {src && (
        <span
          className="absolute font-display leading-none pointer-events-none"
          style={{
            // anchored on the flat crown of the shell (traced against the
            // source photo), not the bounding box's geometric center —
            // the box's center falls on the facemask/chin, not the dome.
            top: '34%', left: `${leftPct}%`, transform: 'translate(-50%, -50%)',
            fontSize: `${sizePx * factor}px`, fontWeight: 900, color: '#fff',
            textShadow: '0 1px 2px rgba(0,0,0,0.35)',
            letterSpacing: '-0.01em', whiteSpace: 'nowrap',
          }}
        >
          {label}
        </span>
      )}
    </div>
  );
};
